import { forwardRef } from '@chakra-ui/react-v2';
import { DatePicker as AntdDatePicker } from 'antd';
import locale from 'antd/lib/date-picker/locale/en_US';
import cx from 'classnames';
import moment from 'moment/moment';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { type FieldSuggestion } from '@frontend/api';
import { useDateFormat } from '@frontend/utils';

import { SuffixIconContent } from './SuffixIconContent';
import { SuggestionFooter, SuggestionFooterProps } from './SuggestionFooter';

export type DatePickerProps = Omit<
  React.ComponentProps<typeof AntdDatePicker>,
  'renderExtraFooter' | 'suffixIcon' | 'className' | 'locale'
> & {
  suggestion?: FieldSuggestion;
  suggestionFooterProps?: SuggestionFooterProps;
  suggestionFooterRef?: React.RefObject<HTMLDivElement>;
  onAcceptSuggestion?: () => void;
  onRejectSuggestion?: () => void;
};

export const DatePicker = forwardRef<DatePickerProps, 'div'>(
  (
    {
      placeholder = 'Select date',
      suggestion,
      suggestionFooterRef,
      suggestionFooterProps,
      size = 'middle',
      format,
      onChange: onChangeProp,
      onAcceptSuggestion: onAcceptSuggestionProp,
      onRejectSuggestion,
      value,
      style,
      popupStyle,
      ...restProps
    },
    ref,
  ) => {
    const { dateFormat } = useDateFormat();
    const [localValue, setLocalValue] = useState(value);

    useEffect(() => {
      setLocalValue(value);
    }, [value]);

    const onChange = useCallback<NonNullable<DatePickerProps['onChange']>>(
      (...args) => {
        setLocalValue(args[0]);
        onChangeProp?.(...args);
        onRejectSuggestion?.();
      },
      [onChangeProp, onRejectSuggestion],
    );

    const onAcceptSuggestion = useCallback(() => {
      if (suggestion) {
        setLocalValue(moment(suggestion.suggestion));
      }
      onAcceptSuggestionProp?.();
    }, [onAcceptSuggestionProp, suggestion]);

    const suggestionIsAccepted = useMemo(() => {
      if (
        suggestion &&
        localValue &&
        moment(suggestion.suggestion).isSame(localValue)
      ) {
        return true;
      } else {
        return false;
      }
    }, [localValue, suggestion]);
    const suggestionIsRejected = useMemo(() => {
      if (
        suggestion &&
        localValue &&
        !moment(suggestion.suggestion).isSame(localValue)
      ) {
        return true;
      } else {
        return false;
      }
    }, [localValue, suggestion]);

    return (
      <AntdDatePicker
        className={cx(['common-ant-picker'], {
          'suggestions-available':
            suggestion && !suggestionIsAccepted && !suggestionIsRejected,
          'suggestions-accepted': suggestionIsAccepted,
          'suggestions-rejected': suggestionIsRejected,
        })}
        locale={{ ...locale, lang: { ...locale.lang, ok: 'SAVE' } }}
        placeholder={placeholder}
        size={size}
        ref={ref}
        format={format ?? dateFormat}
        onChange={onChange}
        suffixIcon={
          <SuffixIconContent
            size={size}
            suggestion={suggestion}
            suggestionIsAccepted={suggestionIsAccepted}
            suggestionIsRejected={suggestionIsRejected}
          />
        }
        renderExtraFooter={
          suggestion
            ? () => (
                <SuggestionFooter
                  ref={suggestionFooterRef}
                  format={format ?? dateFormat}
                  suggestion={suggestion}
                  suggestionIsAccepted={suggestionIsAccepted}
                  suggestionIsRejected={suggestionIsRejected}
                  onAcceptSuggestion={onAcceptSuggestion}
                  {...suggestionFooterProps}
                />
              )
            : undefined
        }
        value={value}
        popupStyle={{
          zIndex: 1400,
          ...popupStyle,
        }}
        style={{
          overflow: 'hidden',
          borderColor:
            suggestion && !suggestionIsAccepted && !suggestionIsRejected
              ? '#8C41ED'
              : 'var(--chakra-colors-separator-border)',
          ...style,
        }}
        {...restProps}
      />
    );
  },
);
